'use client';
import React, { useEffect, useRef, useState } from 'react';
import { SAAS_SIGNUP_URL } from '@/lib/saas';

const basicFeatures = [
  'PDV completo', 'Controle de Estoque', 'Financeiro Básico', 'Relatórios Essenciais',
  'Cadastro de Clientes', 'Cadastro de Produtos', 'Backup Automático', 'Suporte por Chat',
  '1 empresa', 'Até 3 usuários', 'Dashboard Básico', 'Caixa Diário',
];

const proFeatures = [
  'Tudo do Básico', 'Mesas e Balcão', 'Cozinha (KDS)', 'Cardápio Digital + QR Code',
  'IA Integrada', 'Relatórios Avançados', 'Gráficos Interativos', 'Multiempresa',
  'Usuários Ilimitados', 'Controle de Permissões', 'Auditoria Completa', 'Integrações',
  'Assinaturas', 'Funcionários + Comissões', 'Suporte Prioritário 24h', 'API Acesso',
];

export default function PricingSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="planos" className="py-24 px-4 sm:px-6 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] blob-primary opacity-20 rounded-full" />
      <div ref={ref} className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/20 bg-primary/5 text-xs font-bold uppercase tracking-widest text-primary mb-6">
            <span className="w-2 h-2 rounded-full bg-primary" />
            Planos
          </span>
          <h2 className="text-section-title font-black text-foreground mb-4">
            Planos que cabem no seu{' '}
            <span className="gradient-text">orçamento</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            14 dias grátis, sem fidelidade, cancele quando quiser. Suporte incluso em todos os planos.
          </p>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
            {['14 dias grátis', 'Sem cartão de crédito', 'Cancele quando quiser', 'Suporte incluso']?.map((badge) => (
              <div key={badge} className="flex items-center gap-2 text-sm text-muted-foreground">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                {badge}
              </div>
            ))}
          </div>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Basic Plan */}
          <div className={`glass-card rounded-2xl p-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="mb-6">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Plano Básico</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-black text-foreground">R$ 99</span>
                <span className="text-xl font-bold text-foreground">,90</span>
                <span className="text-muted-foreground text-sm">/mês</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Ideal para pequenos negócios</p>
            </div>
            <a
              href={`${SAAS_SIGNUP_URL}?trial=14&plan=basic&utm_source=pricing`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary-outline w-full py-3 text-sm text-center block mb-6 font-semibold"
            >
              Começar Teste Gratuito — 14 dias
            </a>
            <div className="space-y-3">
              {basicFeatures?.map((feat) => (
                <div key={feat} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                  <span className="text-sm text-muted-foreground">{feat}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Pro Plan */}
          <div className={`pricing-card-popular rounded-2xl p-8 relative transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Popular badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="bg-primary text-white text-xs font-black uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg">
                Mais Popular
              </span>
            </div>

            <div className="mb-6">
              <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Plano Pro</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-black text-foreground">R$ 119</span>
                <span className="text-xl font-bold text-foreground">,90</span>
                <span className="text-muted-foreground text-sm">/mês</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Para negócios em crescimento</p>
            </div>
            <a
              href={`${SAAS_SIGNUP_URL}?trial=14&plan=pro&utm_source=pricing`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full py-3 text-sm text-center block mb-6"
            >
              Começar Teste Gratuito — 14 dias
            </a>
            <div className="space-y-3">
              {proFeatures?.map((feat) => (
                <div key={feat} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                  <span className="text-sm text-foreground font-medium">{feat}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* NF-e addon */}
        <div className="glass-card rounded-2xl p-6 border border-yellow-400/20 bg-yellow-400/3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-yellow-400/10 border border-yellow-400/20 flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-400">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                </svg>
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">Módulo Adicional — Emissão de Nota Fiscal</p>
                <p className="text-xs text-muted-foreground">NF-e, NFC-e e NFS-e. Cobrado separadamente conforme uso.</p>
              </div>
            </div>
            <a
              href="/contact"
              className="btn-secondary-outline px-5 py-2.5 text-sm whitespace-nowrap"
            >
              Consultar Preço
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}